<script lang="ts">
  import { connectDisplay, connectionStatus, disconnect, lastError } from '../lib/connection';
  import { view } from '../lib/view';
  import { clearHost, lastService, loadHost, rememberService, saveHost, switchHref } from '../lib/session';
  import type { MusicService } from '@banger/shared';
  import { appleSignedIn, createApplePlayer, prepareApple, signInToApple } from '../apple/player';
  import { send } from '../lib/connection';
  import HostLanding from '../display/HostLanding.svelte';
  import Lobby from '../display/Lobby.svelte';
  import Game from '../display/Game.svelte';
  import InviteOnly from '../display/InviteOnly.svelte';
  import {
    activatePlayback, createPlaybackController, setPlaybackController, type PlaybackController,
  } from '../display/playback';
  import { beginLogin, clearCustomClientId, clientId, customClientIdActive, isLoggedIn } from '../spotify/auth';
  import { noSpotify } from '../spotify/flags';
  import { createPlayer } from '../spotify/player';
  import { holdWakeLock } from '../lib/wakeLock';
  import { onMount } from 'svelte';
  import { fitTvWindow, isTvWindow, tvPopup } from '../lib/tvWindow';

  // Keep the big screen awake for the whole party.
  onMount(holdWakeLock);

  // The TV window sizes its viewport to 16:9 (best effort; a second pass after the first resize settles).
  onMount(() => {
    if (!isTvWindow()) return;
    fitTvWindow();
    const t = setTimeout(fitTvWindow, 400);
    return () => clearTimeout(t);
  });

  const INTENT_KEY = 'banger.spotify.intent';
  const FAST_KEY = 'banger.fast';

  /**
   * Dev-only `?fast=1`: new rooms run at 5x speed (the worker honors it only with
   * DEV_FAST=1). Kept in sessionStorage so it survives the Spotify login redirect.
   */
  function fastRooms(): boolean {
    if (!import.meta.env.DEV) return false;
    try {
      if (new URLSearchParams(location.search).get('fast') === '1') sessionStorage.setItem(FAST_KEY, '1');
      return sessionStorage.getItem(FAST_KEY) === '1';
    } catch {
      return false;
    }
  }
  const devFast = fastRooms();

  const saved = loadHost();
  /** The service of the room on screen (from its saved session), else the one the host picked last. */
  const savedService: MusicService = saved?.service ?? 'spotify'; // rooms saved before Apple Music were Spotify
  let service = $state<MusicService>(saved ? savedService : lastService());
  // Load MusicKit early so the Apple sign-in popup opens straight from the click.
  $effect(() => { if (service === 'apple') prepareApple().catch(() => {}); });

  let busy = $state(false);
  let failed = $state('');
  let refused = $state<'not_allowed' | 'not_premium' | null>(null);
  /** Apple Music needs (re)signing in before the player can start. */
  let appleSignin = $state(false);
  let controller = $state<PlaybackController | null>(null);
  let playerLoading = $state(false);

  const devNoSpotify = noSpotify();
  // Reactive: the "own Spotify app" walkthrough can supply a client ID after load.
  let setupMissing = $state(!devNoSpotify && !clientId());
  let customActive = $state(customClientIdActive());
  function refreshClientId() {
    setupMissing = !devNoSpotify && !clientId();
    customActive = customClientIdActive();
  }

  /** The walkthrough saved a custom client ID: log in with it, then create the room as usual. */
  function ownAppLogin() {
    refreshClientId();
    void create('spotify');
  }

  function switchToDefaultApp() {
    clearCustomClientId();
    refreshClientId();
  }

  let playerSetup: Promise<boolean> | null = null;
  let teardownPlayer = () => {};
  let playerGen = 0; // bumped by dropPlayer: a createPlayer result from an older generation is stale
  /** Creates the Spotify player (once at a time) and wires it to the display view. */
  function setupPlayer(): Promise<boolean> {
    playerSetup ??= (async () => {
      playerLoading = true;
      const gen = playerGen;
      let c: PlaybackController | null = null;
      const p = service === 'apple'
        ? await createApplePlayer((e) => {
          if (e === 'apple_signin') appleSignin = true;
          c?.markLost();
        })
        : await createPlayer((e) => {
          if (e === 'sdk_failed') c?.markLost();
          else refused = e;
        });
      if (gen !== playerGen) { // replaced/dropped while connecting: never install it
        if (!('error' in p)) p.disconnect();
        return false;
      }
      playerLoading = false;
      if ('error' in p) {
        playerSetup = null;
        if (p.error === 'sdk_failed') failed = 'Could not start the Spotify player. Try again.';
        else if (p.error === 'apple_failed') failed = 'Could not start Apple Music. Try again.';
        else if (p.error === 'apple_signin') appleSignin = true;
        else refused = p.error;
        return false;
      }
      appleSignin = false;
      c = createPlaybackController({ player: p, send });
      const unsub = view.subscribe((v) => { if (v?.role === 'display') c?.apply(v); });
      teardownPlayer = () => { unsub(); p.disconnect(); };
      setPlaybackController(c);
      controller = c;
      return true;
    })();
    return playerSetup;
  }

  /** Drops a lost/failed player and builds a fresh one (from a click). */
  function reconnectSpotify() {
    dropPlayer();
    failed = '';
    void activatePlayback(); // marks the click so the new player activates once ready
    void setupPlayer();
  }

  function loginToSpotify() {
    // The saved room reconnects by itself after /callback; the player is set up then.
    void beginLogin();
  }

  /** Apple: the sign-in popup (from the click) if needed, then the player. */
  async function ensureApple(): Promise<boolean> {
    if (!(await signInToApple())) {
      failed = 'Apple Music sign-in didn\'t finish. Try again — you need an Apple Music subscription.';
      return false;
    }
    return setupPlayer();
  }

  /** From a click: sign in to Apple Music again and restart its player. */
  async function reconnectApple() {
    failed = '';
    if (!(await signInToApple())) {
      failed = 'Apple Music sign-in didn\'t finish. Try again.';
      return;
    }
    dropPlayer();
    void activatePlayback();
    void setupPlayer();
  }

  /** True when the host may create a room; otherwise login/an error screen takes over. */
  async function ensureSpotify(): Promise<boolean> {
    if (devNoSpotify) return setupPlayer();
    if (setupMissing) return false;
    if (!isLoggedIn()) {
      try { sessionStorage.setItem(INTENT_KEY, 'create'); } catch { /* */ }
      await beginLogin();
      return false;
    }
    return setupPlayer();
  }

  /** Whether this room's player can start without a login click. */
  const canAutoStart = () => (service === 'apple' ? appleSignedIn() : devNoSpotify || isLoggedIn());

  if (saved) {
    connectDisplay(saved.code, saved.hostToken);
    if (savedService === 'apple') prepareApple().then(() => { if (appleSignedIn()) void setupPlayer(); }, () => {});
    else if (canAutoStart()) void setupPlayer();
  } else {
    let intent: string | null = null;
    try { intent = sessionStorage.getItem(INTENT_KEY); sessionStorage.removeItem(INTENT_KEY); } catch { /* */ }
    if (intent === 'create' && isLoggedIn()) void create('spotify'); // back from the Spotify login
  }

  const attention = $derived(controller ? controller.attention : null);

  async function create(svc: MusicService) {
    busy = true;
    failed = '';
    if (svc !== service) dropPlayer();
    service = svc;
    rememberService(svc);
    try {
      if (!(await (svc === 'apple' ? ensureApple() : ensureSpotify()))) return;
      const q = new URLSearchParams({ service: svc, ...(devFast ? { fast: '1' } : {}) });
      const res = await fetch(`/api/rooms?${q}`, { method: 'POST' });
      if (!res.ok) throw new Error(String(res.status));
      const h = (await res.json()) as { code: string; hostToken: string };
      saveHost({ ...h, service: svc });
      connectDisplay(h.code, h.hostToken);
    } catch {
      failed = 'Could not create a room. Try again.';
    } finally {
      busy = false;
    }
  }

  /** From the invite-only screen: leave the Spotify room and host a fresh Apple Music one. */
  function switchToApple() {
    refused = null;
    dropPlayer();
    clearHost();
    disconnect();
    view.set(null);
    void create('apple');
  }

  /** Finale "New room": forget this room and go back to the landing page. */
  function newRoom() {
    clearHost();
    disconnect();
    view.set(null);
  }

  /** Another tab took this room's display over; reconnecting here takes it back. */
  function useThisTab() {
    const h = loadHost();
    if (h) {
      connectDisplay(h.code, h.hostToken);
      if (canAutoStart()) void setupPlayer();
    } else lastError.set(null);
  }

  function dropPlayer() {
    playerGen += 1;
    playerLoading = false;
    teardownPlayer();
    teardownPlayer = () => {};
    setPlaybackController(null);
    controller = null;
    playerSetup = null;
  }

  const dv = $derived($view && $view.role === 'display' ? $view : null);
  /** This tab has shown a room; before that, a saved room that is gone is just stale, not news. */
  let hadRoom = $state(false);
  $effect(() => { if (dv) hadRoom = true; });
  const STALE = new Set(['ROOM_NOT_FOUND', 'BAD_TOKEN']);
  const shownError = $derived(
    $lastError && !(STALE.has($lastError.code) && !hadRoom) ? $lastError.message : '',
  );
  const replaced = $derived(!dv && $lastError?.code === 'REPLACED');
  // Only one audio player: a replaced display (e.g. the TV window took over) lets go of Spotify.
  $effect(() => { if (replaced) dropPlayer(); });
  const tvOpen = $derived(!!$tvPopup);
  const connecting = $derived($connectionStatus === 'connecting' || $connectionStatus === 'reconnecting');
</script>

{#if tvOpen}
  <main class="progress">
    <h1 class="wordmark">BANGER!</h1>
    <p>BANGER! is open in the TV window. Cast that window to your TV (⋮ → Cast… → Cast tab). You can close this tab.</p>
    <button class="btn-neon use" onclick={() => $tvPopup?.focus()}>Focus TV window</button>
  </main>
{:else if refused}
  <InviteOnly reason={refused} onapple={switchToApple} />
{:else if replaced}
  <main class="progress">
    <h1 class="wordmark">BANGER!</h1>
    <p>{isTvWindow() ? 'BANGER! moved to another tab or window.' : 'BANGER! is running in the TV window. Cast that window to your TV — or press Use this tab to bring it back here.'}</p>
    <button class="btn-neon use" onclick={useThisTab}>Use this tab</button>
  </main>
{:else if dv && dv.phase === 'lobby'}
  <Lobby view={dv} />
{:else if dv}
  <Game view={dv} onnewroom={newRoom} />
{:else if connecting && !busy}
  <main class="progress"><h1 class="wordmark">BANGER!</h1><p>Connecting…</p></main>
{:else}
  <HostLanding {busy} {setupMissing} {customActive} {service} error={failed || shownError} onhost={create}
    onownlogin={ownAppLogin} onswitchback={switchToDefaultApp} />
  <p class="switch"><a href={switchHref('player')}>Join as a player instead</a></p>
{/if}

{#if dv && service === 'apple' && !tvOpen}
  {#if !controller}
    {#if playerLoading}
      <p class="unlock status">Connecting to Apple Music…</p>
    {:else}
      <button class="btn-neon unlock" onclick={reconnectApple}>{appleSignin ? 'Sign in to Apple Music' : 'Reconnect Apple Music'}</button>
    {/if}
  {:else if $attention === 'reconnect'}
    <button class="btn-neon unlock" onclick={reconnectApple}>Apple Music player lost — click to reconnect</button>
  {:else if $attention === 'activate'}
    <button class="btn-neon unlock" onclick={() => controller?.activate({ gesture: true })}>Click to play the music</button>
  {/if}
  {#if failed}<p class="unlock-err">{failed}</p>{/if}
{:else if dv && !refused && !devNoSpotify && !tvOpen}
  {#if !controller}
    {#if playerLoading}
      <p class="unlock status">Connecting to Spotify…</p>
    {:else if !isLoggedIn()}
      <button class="btn-neon unlock" onclick={loginToSpotify}>Log in to Spotify</button>
    {:else}
      <button class="btn-neon unlock" onclick={reconnectSpotify}>Reconnect Spotify</button>
    {/if}
  {:else if $attention === 'reconnect'}
    <button class="btn-neon unlock" onclick={reconnectSpotify}>Spotify player lost — click to reconnect</button>
  {:else if $attention === 'activate'}
    <button class="btn-neon unlock" onclick={() => controller?.activate({ gesture: true })}>Click to play the music</button>
  {/if}
{/if}

<style>
  .progress { min-height: 100%; display: flex; flex-direction: column; gap: 1rem; align-items: center; justify-content: center; text-align: center; font-size: 1.6rem; }
  h1 { margin: 0; font-size: 5rem; }
  .unlock { position: fixed; top: 16px; left: 50%; transform: translateX(-50%); font-size: 1.6rem; padding: 0.5em 1.6em; z-index: 10; }
  .use { font-size: 1.6rem; padding: 0.5em 1.6em; }
  .status { margin: 0; font-size: 1.3rem; opacity: 0.8; }
  .unlock-err { position: fixed; top: 84px; left: 50%; transform: translateX(-50%); margin: 0; color: #ff8fa3; font-weight: 700; z-index: 10; }
  .switch { position: fixed; bottom: 12px; right: 16px; margin: 0; }
</style>
